import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Upload, Plus, LogOut, Image, Video, Type, ArrowLeft, RefreshCw, FileImage, Film, Link2, Check, Loader2, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks

const SECTIONS = [
  { key: 'hero', label: 'Hero Background' },
  { key: 'sponsors', label: 'Sponsors' },
  { key: 'media_story', label: 'Media Story' },
  { key: 'experience', label: 'Experience Gallery' },
];

const MAX_FILE_SIZE = 1024 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska', 'video/x-m4v', 'video/3gpp', 'video/x-flv', 'video/x-ms-wmv'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.mp4', '.mov', '.avi', '.webm', '.mkv', '.m4v', '.3gp', '.flv', '.wmv'];

function validateFile(file, type = 'any') {
  if (file.size > MAX_FILE_SIZE) {
    return `File too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum size is 1GB.`;
  }
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  const isAllowedByExt = ALLOWED_EXTENSIONS.includes(ext);
  const isAllowedImage = ALLOWED_IMAGE_TYPES.includes(file.type) || (type !== 'video' && ['.jpg','.jpeg','.png','.gif','.webp','.svg'].includes(ext));
  const isAllowedVideo = ALLOWED_VIDEO_TYPES.includes(file.type) || file.type.startsWith('video/') || (type !== 'image' && ['.mp4','.mov','.avi','.webm','.mkv','.m4v','.3gp','.flv','.wmv'].includes(ext));
  if (type === 'image' && !isAllowedImage) {
    return 'Unsupported image format. Use JPG, PNG, GIF, WebP, or SVG.';
  }
  if (type === 'video' && !isAllowedVideo) {
    return 'Unsupported video format. Use MP4, MOV, AVI, or WebM.';
  }
  if (type === 'any' && !isAllowedImage && !isAllowedVideo && !isAllowedByExt) {
    return 'Unsupported file type. Use image (JPG, PNG, GIF, WebP, SVG) or video (MP4, MOV, AVI, WebM) files.';
  }
  return null;
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [pinVerified, setPinVerified] = useState(false);
  const [step, setStep] = useState(token ? 'pin' : 'login');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [pin, setPin] = useState(['', '', '', '']);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [contacts, setContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (token && pinVerified) fetchMedia();
  }, [token, pinVerified]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/media`, { headers });
      setMedia(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, { headers });
      setContacts(res.data);
      setShowContacts(true);
    } catch (err) {
      toast.error('Failed to fetch contacts');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, loginForm);
      setToken(res.data.token);
      localStorage.setItem('admin_token', res.data.token);
      setStep('pin');
    } catch (err) {
      toast.error('Invalid credentials');
    }
  };

  const handlePinChange = (index, value) => {
    if (value.length > 1) return;
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    if (value && index < 3) {
      document.getElementById(`pin-${index + 1}`)?.focus();
    }
    if (newPin.every(d => d !== '')) {
      verifyPin(newPin.join(''));
    }
  };

  const verifyPin = async (pinCode) => {
    try {
      await axios.post(`${API}/admin/verify-pin`, { pin: pinCode }, { headers });
      setPinVerified(true);
      setStep('dashboard');
    } catch (err) {
      toast.error('Invalid PIN');
      setPin(['', '', '', '']);
      document.getElementById('pin-0')?.focus();
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Delete this media item?')) return;
    try {
      await axios.delete(`${API}/media/${mediaId}`, { headers });
      setMedia(media.filter(m => m.id !== mediaId));
      toast.success('Deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const getUploadErrorMessage = (err) => {
    if (!err.response) {
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        return 'Upload timed out. Try a smaller file or check your connection.';
      }
      return 'Network error. Check your internet connection and try again.';
    }
    const status = err.response.status;
    const data = err.response.data;
    const detail = typeof data === 'object' ? data?.detail : typeof data === 'string' ? data.substring(0, 200) : null;
    if (status === 413) return 'File too large for the server. Try a smaller file (under 100MB recommended).';
    if (status === 408 || status === 504) return 'Upload timed out. Try a smaller file.';
    if (status === 401 || status === 403) return 'Session expired. Please refresh and log in again.';
    if (status === 400) return detail || 'Invalid file. Please check the format and try again.';
    if (detail) return detail;
    return `Upload failed (HTTP ${status}). Please try again.`;
  };

  const uploadChunked = async (file, onProgress) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = crypto.randomUUID();
    let uploadedBytes = 0;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const formData = new FormData();
      formData.append('file', chunk, file.name);
      formData.append('upload_id', uploadId);
      formData.append('chunk_index', i.toString());
      formData.append('total_chunks', totalChunks.toString());
      formData.append('filename', file.name);

      await axios.post(`${API}/media/upload-chunk`, formData, {
        headers,
        timeout: 0,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      uploadedBytes += (end - start);
      if (onProgress) onProgress(Math.round((uploadedBytes * 100) / file.size));
    }

    const completeForm = new FormData();
    completeForm.append('upload_id', uploadId);
    completeForm.append('total_chunks', totalChunks.toString());
    completeForm.append('filename', file.name);
    const res = await axios.post(`${API}/media/upload-complete`, completeForm, { headers });
    return res.data;
  };

  const uploadFile = async (file, onProgress) => {
    if (file.size > 50 * 1024 * 1024) {
      return await uploadChunked(file, onProgress);
    }
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API}/media/upload`, formData, {
      headers,
      timeout: 0,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total)); }
    });
    return res.data;
  };

  const handleUpload = async (file, section, onProgress) => {
    const error = validateFile(file);
    if (error) { toast.error(error); return; }
    try {
      const uploadRes = await uploadFile(file, onProgress);
      const mediaType = file.type.startsWith('video') ? 'video' : 'image';
      const createRes = await axios.post(`${API}/media`, {
        section,
        media_type: mediaType,
        url: uploadRes.url,
        optimized_url: uploadRes.optimized_url || '',
        thumb_url: uploadRes.thumb_url || '',
        title: file.name.split('.')[0],
        order: media.filter(m => m.section === section).length,
      }, { headers });
      setMedia([...media, createRes.data]);
      toast.success('File uploaded successfully');
    } catch (err) {
      toast.error(getUploadErrorMessage(err));
    }
  };

  const handleReplace = async (mediaId, file, onProgress) => {
    const error = validateFile(file);
    if (error) { toast.error(error); throw new Error(error); }
    try {
      const uploadRes = await uploadFile(file, onProgress);
      const mediaType = file.type.startsWith('video') ? 'video' : 'image';
      const res = await axios.put(`${API}/media/${mediaId}`, {
        url: uploadRes.url,
        optimized_url: uploadRes.optimized_url || '',
        thumb_url: uploadRes.thumb_url || '',
        media_type: mediaType,
        video_url: '',
      }, { headers });
      setMedia(media.map(m => m.id === mediaId ? res.data : m));
      toast.success('Media replaced successfully');
    } catch (err) {
      toast.error(getUploadErrorMessage(err));
      throw err;
    }
  };

  const handleReplaceVideoUrl = async (mediaId, videoUrl) => {
    try {
      const res = await axios.put(`${API}/media/${mediaId}`, {
        video_url: videoUrl,
        media_type: 'video',
      }, { headers });
      setMedia(media.map(m => m.id === mediaId ? res.data : m));
      toast.success('Video URL saved successfully');
    } catch (err) {
      toast.error('Failed to save video URL. Please try again.');
      throw err;
    }
  };

  const handleAddVideoUrl = async (section, videoUrl, title) => {
    try {
      const res = await axios.post(`${API}/media`, {
        section,
        media_type: 'video',
        video_url: videoUrl,
        title: title || 'Video',
        order: media.filter(m => m.section === section).length,
      }, { headers });
      setMedia([...media, res.data]);
      toast.success('Video link added');
    } catch (err) {
      toast.error('Failed to add video');
    }
  };

  const handleUpdate = async (mediaId, updates) => {
    try {
      const res = await axios.put(`${API}/media/${mediaId}`, updates, { headers });
      setMedia(media.map(m => m.id === mediaId ? res.data : m));
      toast.success('Saved');
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const handleAddSponsor = async (name) => {
    try {
      const res = await axios.post(`${API}/media`, {
        section: 'sponsors',
        media_type: 'text',
        title: name,
        order: media.filter(m => m.section === 'sponsors').length,
      }, { headers });
      setMedia([...media, res.data]);
      toast.success('Sponsor added');
    } catch (err) {
      toast.error('Failed to add sponsor');
    }
  };

  const handleLogout = () => {
    setToken('');
    setPinVerified(false);
    setStep('login');
    localStorage.removeItem('admin_token');
  };

  const sectionMedia = media.filter(m => m.section === activeSection);

  // --- LOGIN ---
  if (step === 'login') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-sm">
          <div className="text-center mb-14">
            <h1 className="font-heading text-3xl text-gold tracking-[0.2em]">OZFEST</h1>
            <p className="text-zinc-600 text-xs font-body mt-2 tracking-wider">Admin Portal</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
            <input
              type="text"
              placeholder="Username"
              value={loginForm.username}
              onChange={e => setLoginForm({...loginForm, username: e.target.value})}
              className="w-full bg-transparent border-b border-white/15 focus:border-gold text-white placeholder-zinc-700 py-3 outline-none transition-colors duration-300 font-body text-sm"
              data-testid="admin-username"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={loginForm.password}
              onChange={e => setLoginForm({...loginForm, password: e.target.value})}
              className="w-full bg-transparent border-b border-white/15 focus:border-gold text-white placeholder-zinc-700 py-3 outline-none transition-colors duration-300 font-body text-sm"
              data-testid="admin-password"
              required
            />
            <button
              type="submit"
              className="w-full py-3.5 bg-gold text-black font-heading text-xs tracking-[0.25em] uppercase hover:bg-white transition-colors duration-300 mt-4"
              data-testid="admin-login-btn"
            >
              Sign In
            </button>
          </form>
          <div className="text-center mt-8">
            <a href="/" className="text-zinc-700 text-xs font-body hover:text-gold transition-colors inline-flex items-center gap-2">
              <ArrowLeft className="w-3 h-3" /> Back to site
            </a>
          </div>
        </div>
      </div>
    );
  }

  // --- PIN ---
  if (step === 'pin') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="admin-pin-page">
        <div className="text-center">
          <h1 className="font-heading text-3xl text-gold tracking-[0.2em] mb-2">OZFEST</h1>
          <p className="text-zinc-600 text-xs font-body mb-14 tracking-wider">Enter 4-digit security PIN</p>
          <div className="flex gap-4 justify-center" data-testid="pin-inputs">
            {pin.map((digit, i) => (
              <input
                key={i}
                id={`pin-${i}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handlePinChange(i, e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => {
                  if (e.key === 'Backspace' && !digit && i > 0) {
                    document.getElementById(`pin-${i - 1}`)?.focus();
                  }
                }}
                className="w-12 h-12 text-center text-lg font-heading bg-transparent border border-white/15 focus:border-gold text-white outline-none transition-colors duration-300"
                data-testid={`pin-digit-${i}`}
              />
            ))}
          </div>
          <button
            onClick={handleLogout}
            className="mt-10 text-zinc-700 text-xs font-body hover:text-gold transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // --- DASHBOARD ---
  return (
    <div className="min-h-screen bg-black text-white font-body" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b border-white/[0.06]">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-base text-gold tracking-[0.15em]">OZFEST</h1>
          <span className="text-zinc-700 text-[10px] tracking-wider uppercase">Admin</span>
        </div>
        <div className="flex items-center gap-5">
          <button
            onClick={fetchContacts}
            className="text-[10px] text-zinc-600 hover:text-gold transition-colors tracking-wider uppercase font-body"
            data-testid="view-contacts-btn"
          >
            Contacts
          </button>
          <a href="/" className="text-[10px] text-zinc-600 hover:text-gold transition-colors tracking-wider uppercase font-body">
            View Site
          </a>
          <button onClick={handleLogout} className="text-zinc-600 hover:text-gold transition-colors" data-testid="admin-logout-btn">
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-52 border-r border-white/[0.04] min-h-[calc(100vh-49px)] p-4">
          <p className="text-[9px] text-zinc-700 uppercase tracking-[0.2em] mb-4">Media Sections</p>
          {SECTIONS.map(s => (
            <button
              key={s.key}
              onClick={() => { setActiveSection(s.key); setShowContacts(false); }}
              className={`block w-full text-left px-3 py-2.5 text-xs mb-0.5 transition-colors duration-200 ${
                activeSection === s.key && !showContacts
                  ? 'text-gold bg-white/[0.03]' : 'text-zinc-600 hover:text-zinc-300'
              }`}
              data-testid={`section-tab-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Main */}
        <div className="flex-1 p-6">
          {showContacts ? (
            <ContactsList contacts={contacts} />
          ) : (
            <MediaManager
              section={activeSection}
              media={sectionMedia}
              onDelete={handleDelete}
              onUpload={handleUpload}
              onReplace={handleReplace}
              onUpdate={handleUpdate}
              onAddVideoUrl={handleAddVideoUrl}
              onReplaceVideoUrl={handleReplaceVideoUrl}
              onAddSponsor={handleAddSponsor}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ContactsList({ contacts }) {
  return (
    <div data-testid="contacts-list">
      <h2 className="font-heading text-lg uppercase tracking-wider mb-6">Contact Submissions</h2>
      {contacts.length === 0 ? (
        <p className="text-zinc-700 text-sm">No submissions yet.</p>
      ) : (
        <div className="space-y-3">
          {contacts.map(c => (
            <div key={c.id} className="p-4 border border-white/[0.06] space-y-1.5">
              <div className="flex justify-between items-baseline">
                <span className="text-gold font-heading text-sm tracking-wider">{c.name}</span>
                <span className="text-zinc-700 text-[10px]">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-zinc-500 text-xs">{c.email}</p>
              {c.interest && <p className="text-zinc-600 text-[10px] uppercase tracking-wider">{c.interest}</p>}
              {c.message && <p className="text-zinc-400 text-xs mt-1">{c.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MediaManager({ section, media, onDelete, onUpload, onReplace, onUpdate, onAddVideoUrl, onReplaceVideoUrl, onAddSponsor }) {
  const [showAddNew, setShowAddNew] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [videoTitleInput, setVideoTitleInput] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [addingMedia, setAddingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const sectionLabel = SECTIONS.find(s => s.key === section)?.label || section;

  const handleNewUpload = async (file) => {
    const error = validateFile(file);
    if (error) { toast.error(error); return; }
    setAddingMedia(true);
    setUploadProgress(0);
    try {
      await onUpload(file, section, (pct) => setUploadProgress(pct));
      setShowAddNew(false);
    } catch {
      // handled
    } finally {
      setAddingMedia(false);
      setUploadProgress(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-lg uppercase tracking-wider">{sectionLabel}</h2>
          <p className="text-[10px] text-zinc-700 mt-1">{media.length} item{media.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setShowAddNew(!showAddNew)}
          className={`flex items-center gap-2 px-4 py-2 text-xs transition-colors ${
            showAddNew ? 'bg-gold/15 text-gold' : 'bg-white/[0.04] text-zinc-400 hover:bg-white/[0.08] hover:text-white'
          }`}
          data-testid="add-new-media-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          Add New
        </button>
      </div>

      {showAddNew && (
        <div className="mb-6 p-4 border border-white/[0.06] bg-white/[0.01] space-y-3" data-testid="add-new-panel">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-3">Add new media to this section</p>
          <div className="flex gap-2">
            <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 border border-white/[0.06] cursor-pointer hover:bg-white/[0.04] transition-colors ${addingMedia ? 'opacity-50 pointer-events-none' : ''}`}>
              <FileImage className="w-4 h-4 text-zinc-500" />
              <span className="text-[10px] text-zinc-400 tracking-wider uppercase">Upload Photo</span>
              <input type="file" className="hidden" accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                onChange={e => { if (e.target.files[0]) { handleNewUpload(e.target.files[0]); e.target.value = ''; }}}
                data-testid="add-upload-photo"
              />
            </label>
            <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 border border-white/[0.06] cursor-pointer hover:bg-white/[0.04] transition-colors ${addingMedia ? 'opacity-50 pointer-events-none' : ''}`}>
              <Film className="w-4 h-4 text-zinc-500" />
              <span className="text-[10px] text-zinc-400 tracking-wider uppercase">Upload Video</span>
              <input type="file" className="hidden" accept="video/*,.mp4,.mov,.avi,.webm,.mkv,.m4v"
                onChange={e => { if (e.target.files[0]) { handleNewUpload(e.target.files[0]); e.target.value = ''; }}}
                data-testid="add-upload-video"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <input type="text" placeholder="YouTube or Vimeo URL" value={videoUrlInput}
              onChange={e => setVideoUrlInput(e.target.value)}
              className="flex-1 bg-transparent border border-white/[0.06] text-white placeholder-zinc-700 px-3 py-2.5 text-[11px] outline-none focus:border-gold/50 transition-colors"
              data-testid="add-video-url-input"
            />
            <input type="text" placeholder="Title" value={videoTitleInput}
              onChange={e => setVideoTitleInput(e.target.value)}
              className="w-32 bg-transparent border border-white/[0.06] text-white placeholder-zinc-700 px-3 py-2.5 text-[11px] outline-none focus:border-gold/50 transition-colors"
            />
            <button onClick={() => {
              if (videoUrlInput) {
                onAddVideoUrl(section, videoUrlInput, videoTitleInput);
                setVideoUrlInput(''); setVideoTitleInput('');
              }
            }} className="px-3 py-2 bg-gold/10 text-gold hover:bg-gold/20 transition-colors" data-testid="add-video-url-btn">
              <Link2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {section === 'sponsors' && (
            <div className="flex gap-2 pt-1">
              <input type="text" placeholder="Sponsor name (text placeholder)" value={sponsorName}
                onChange={e => setSponsorName(e.target.value)}
                className="flex-1 bg-transparent border border-white/[0.06] text-white placeholder-zinc-700 px-3 py-2.5 text-[11px] outline-none focus:border-gold/50 transition-colors"
                data-testid="add-sponsor-name"
              />
              <button onClick={() => { if (sponsorName) { onAddSponsor(sponsorName); setSponsorName(''); }}}
                className="px-4 py-2 bg-gold/10 text-gold text-[10px] tracking-wider uppercase hover:bg-gold/20 transition-colors"
                data-testid="add-sponsor-btn"
              >
                Add
              </button>
            </div>
          )}
          {addingMedia && (
            <div className="space-y-2" data-testid="add-new-upload-progress">
              <div className="flex items-center gap-2 text-gold text-[10px]">
                {uploadProgress !== null && uploadProgress < 100 ? (
                  <span className="tracking-wider">Uploading... {uploadProgress}%</span>
                ) : uploadProgress === 100 ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /><span className="tracking-wider">Processing...</span></>
                ) : (
                  <><Loader2 className="w-3 h-3 animate-spin" /><span className="tracking-wider">Uploading...</span></>
                )}
              </div>
              {uploadProgress !== null && (
                <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gold rounded-full transition-all duration-200 ease-out" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </div>
          )}
          <p className="text-[9px] text-zinc-700">Supported: JPG, PNG, GIF, WebP, SVG, MP4, MOV, AVI, WebM. Max 1GB.</p>
        </div>
      )}

      {media.length === 0 ? (
        <div className="text-center py-20 text-zinc-700">
          <Image className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-xs">No media in this section yet.</p>
          <p className="text-[10px] text-zinc-800 mt-1">Click "Add New" to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {media.map(item => (
            <MediaCard
              key={item.id}
              item={item}
              onDelete={() => onDelete(item.id)}
              onUpdate={(updates) => onUpdate(item.id, updates)}
              onReplace={(file, onProgress) => onReplace(item.id, file, onProgress)}
              onReplaceVideoUrl={(url) => onReplaceVideoUrl(item.id, url)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MediaCard({ item, onDelete, onUpdate, onReplace, onReplaceVideoUrl }) {
  const [mode, setMode] = useState(null);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || '');
  const [websiteUrl, setWebsiteUrl] = useState(item.website_url || '');
  const [videoUrl, setVideoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  useEffect(() => {
    setTitle(item.title);
    setDescription(item.description || '');
    setWebsiteUrl(item.website_url || '');
  }, [item]);

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  const handleFileReplace = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    try {
      await onReplace(file, (pct) => setUploadProgress(pct));
      setMode(null);
    } catch {
      // Error already shown by parent toast
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleVideoUrlReplace = async () => {
    if (!videoUrl.trim()) {
      toast.error('Please enter a video URL');
      return;
    }
    setUploading(true);
    try {
      await onReplaceVideoUrl(videoUrl);
      setVideoUrl('');
      setMode(null);
    } catch {
      // Error already shown by parent toast
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-white/[0.06] bg-[#030303]" data-testid={`media-card-${item.id}`}>
      {/* Preview */}
      <div className="aspect-[4/3] bg-[#060606] relative overflow-hidden">
        {item.media_type === 'text' ? (
          <div className="w-full h-full flex items-center justify-center p-4">
            <span className="font-heading text-sm text-zinc-500 tracking-[0.15em] text-center uppercase">{item.title}</span>
          </div>
        ) : (item.media_type === 'video' && item.url && !item.video_url) ? (
          <>
            <video
              src={`${resolveUrl(item.url)}#t=0.1`}
              className="w-full h-full object-cover"
              preload="metadata"
              muted
              playsInline
              onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'flex'); }}
            />
            <div className="w-full h-full items-center justify-center gap-2 hidden" style={{ display: 'none' }}>
              <Film className="w-8 h-8 text-gold/30" />
              <span className="text-[9px] text-zinc-500">Video uploaded</span>
            </div>
            <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/70 px-2 py-1 rounded">
              <Film className="w-3 h-3 text-gold" />
              <span className="text-[9px] text-gold tracking-wider uppercase">Video</span>
            </div>
          </>
        ) : item.url ? (
          <img src={resolveUrl(item.url)} alt={item.title} className="w-full h-full object-cover" />
        ) : item.video_url ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <Film className="w-8 h-8 text-gold/30" />
            <span className="text-[9px] text-zinc-600 max-w-full truncate px-2">{item.video_url}</span>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-8 h-8 text-zinc-800" />
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-2.5" data-testid={`upload-progress-${item.id}`}>
            {uploadProgress !== null && uploadProgress < 100 ? (
              <>
                <span className="text-gold text-xs font-heading tracking-wider">{uploadProgress}%</span>
                <div className="w-3/4 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gold rounded-full transition-all duration-200 ease-out" style={{ width: `${uploadProgress}%` }} />
                </div>
                <span className="text-zinc-500 text-[9px] tracking-wider uppercase">Uploading</span>
              </>
            ) : uploadProgress === 100 ? (
              <>
                <Loader2 className="w-5 h-5 text-gold animate-spin" />
                <span className="text-zinc-500 text-[9px] tracking-wider uppercase">Processing</span>
              </>
            ) : (
              <Loader2 className="w-6 h-6 text-gold animate-spin" />
            )}
          </div>
        )}
      </div>

      {/* Info + Actions */}
      <div className="p-3">
        <p className="text-[11px] text-zinc-400 truncate mb-3 font-medium">{item.title || 'Untitled'}</p>
        {item.website_url && (
          <p className="text-[9px] text-gold/50 truncate -mt-2 mb-3">{item.website_url}</p>
        )}

        {/* 4 Action Buttons */}
        <div className="flex gap-1.5">
          <button
            onClick={onDelete}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-500/[0.07] text-red-400/70 hover:bg-red-500/15 hover:text-red-400 transition-all text-[10px] tracking-wider uppercase"
            data-testid={`delete-media-${item.id}`}
          >
            <Trash2 className="w-3 h-3" />
            <span className="hidden sm:inline">Delete</span>
          </button>
          <button
            onClick={() => setMode(mode === 'edit' ? null : 'edit')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 transition-all text-[10px] tracking-wider uppercase ${
              mode === 'edit' ? 'bg-gold/20 text-gold' : 'bg-white/[0.04] text-zinc-500 hover:bg-white/[0.08] hover:text-white'
            }`}
            data-testid={`edit-media-${item.id}`}
          >
            <Type className="w-3 h-3" />
            <span className="hidden sm:inline">Edit</span>
          </button>
          <button
            onClick={() => setMode(mode === 'replace' ? null : 'replace')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 transition-all text-[10px] tracking-wider uppercase ${
              mode === 'replace' ? 'bg-gold/20 text-gold' : 'bg-white/[0.04] text-zinc-500 hover:bg-white/[0.08] hover:text-white'
            }`}
            data-testid={`replace-media-${item.id}`}
          >
            <RefreshCw className="w-3 h-3" />
            <span className="hidden sm:inline">Replace</span>
          </button>
          <button
            onClick={() => { onUpdate({ title, description, website_url: websiteUrl }); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gold/[0.08] text-gold/70 hover:bg-gold/20 hover:text-gold transition-all text-[10px] tracking-wider uppercase"
            data-testid={`save-media-${item.id}`}
          >
            <Save className="w-3 h-3" />
            <span className="hidden sm:inline">Save</span>
          </button>
        </div>

        {/* Edit Panel */}
        {mode === 'edit' && (
          <div className="mt-3 pt-3 border-t border-white/[0.04] space-y-2" data-testid={`edit-panel-${item.id}`}>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Title"
              className="w-full bg-transparent border border-white/[0.08] text-white text-[11px] px-2.5 py-2 outline-none focus:border-gold/50 transition-colors"
            />
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Description / caption"
              className="w-full bg-transparent border border-white/[0.08] text-white text-[11px] px-2.5 py-2 outline-none focus:border-gold/50 transition-colors"
            />
            {item.section === 'sponsors' && (
              <input
                type="text"
                value={websiteUrl}
                onChange={e => setWebsiteUrl(e.target.value)}
                placeholder="Website URL (e.g. https://sponsor.com)"
                className="w-full bg-transparent border border-white/[0.08] text-white text-[11px] px-2.5 py-2 outline-none focus:border-gold/50 transition-colors"
                data-testid={`website-url-${item.id}`}
              />
            )}
            <button
              onClick={() => { onUpdate({ title, description, website_url: websiteUrl }); setMode(null); }}
              className="w-full py-2 bg-gold/15 text-gold text-[10px] tracking-wider uppercase hover:bg-gold/25 transition-colors flex items-center justify-center gap-1.5"
              data-testid={`save-edit-${item.id}`}
            >
              <Check className="w-3 h-3" />
              Save Changes
            </button>
          </div>
        )}

        {/* Replace Panel */}
        {mode === 'replace' && (
          <div className="mt-3 pt-3 border-t border-white/[0.04] space-y-2" data-testid={`replace-panel-${item.id}`}>
            <label className="flex items-center gap-2 px-3 py-2.5 bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer transition-colors border border-white/[0.06]">
              <FileImage className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-[10px] text-zinc-400 tracking-wider uppercase">Upload Photo</span>
              <input
                type="file"
                className="hidden"
                accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                onChange={e => { if (e.target.files[0]) { handleFileReplace(e.target.files[0]); e.target.value = ''; }}}
                data-testid={`upload-photo-${item.id}`}
              />
            </label>
            <label className="flex items-center gap-2 px-3 py-2.5 bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer transition-colors border border-white/[0.06]">
              <Film className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-[10px] text-zinc-400 tracking-wider uppercase">Upload Video</span>
              <input
                type="file"
                className="hidden"
                accept="video/*,.mp4,.mov,.avi,.webm,.mkv,.m4v"
                onChange={e => { if (e.target.files[0]) { handleFileReplace(e.target.files[0]); e.target.value = ''; }}}
                data-testid={`upload-video-${item.id}`}
              />
            </label>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={videoUrl}
                onChange={e => setVideoUrl(e.target.value)}
                placeholder="YouTube / Vimeo URL"
                className="flex-1 bg-transparent border border-white/[0.06] text-white text-[10px] px-2.5 py-2 outline-none focus:border-gold/50 transition-colors"
                data-testid={`video-url-replace-${item.id}`}
              />
              <button
                onClick={handleVideoUrlReplace}
                className="px-3 py-2 bg-gold/10 text-gold text-[10px] hover:bg-gold/20 transition-colors flex items-center gap-1"
                data-testid={`save-video-url-${item.id}`}
              >
                <Link2 className="w-3 h-3" />
                Save
              </button>
            </div>
            <p className="text-[9px] text-zinc-700">Max 1GB. Images: JPG, PNG, GIF, WebP. Video: MP4, MOV, WebM.</p>
          </div>
        )}
      </div>
    </div>
  );
}
